import { Accordion, Button, Table } from '@bphxd/ds-core-react';
import React from 'react';
import { CertificateTypeEnum } from '../../../graph-queries/__generated__/graphql';
import { TCertificate } from '../../../lib/types';
import { TCertificateFilters } from '../../organisms/CertificateFilter/CertificateFiltersModal';
import classes from './CertificateMonitoring.module.scss';
import { isAllComplete } from './CertificateMonitoring.util';
import { getCertificateAreas } from '../../../lib/util/certificateOptions';
import { useSelectedAsset } from '../../../lib/util';

const headers = [
    '',
    'Broken Out',
    'Assembled',
    'Tightened/Inspected',
    'Tested',
    'Started',
    'Ready To Complete',
];

/** 
 Counts the number of certificates for a given area where all joints have the specificed step signed.
 @param certificates - list of all in-progress certificates
 @param area - area to filter certificates by
 @param stepNo - represents step to check if signed for all joints
 **/
const getCertCount = (certificates: TCertificate[], area: string, stepNo: number): number => {
    // filter certificates by area
    const filteredCertificates = certificates.filter((cert) => cert.area === area);

    let actualStepNo = stepNo;

    // filter certificates where all joints have the specified step signed
    const requiredCerts = filteredCertificates.filter((cert) => {
        if (stepNo === 3) {
            const isSbt = cert.type === CertificateTypeEnum.Sbt;
            actualStepNo = isSbt ? 4 : stepNo; // for SBT, use stepNo 4 (Witnessed) instead of 3 (Tightened)
        }

        const allJoints = cert.tarJoints || [];

        if (allJoints.length === 0) return false;

        // check if all joints have the step signed
        return allJoints.every((joint) => {
            const allSteps = joint.tarJointInstallationSteps || [];
            const step = allSteps.find((s) => s.stepNo === actualStepNo);
            return step?.signedBy && step.signedBy.trim().length > 0
        });
    });
    return requiredCerts.length;
};

interface CertificateMonitoringDashboardProps {
    certificates: TCertificate[];
    onSubmit: (filters: TCertificateFilters) => void;
}

export const CertificateMonitoringDashboard: React.FC<CertificateMonitoringDashboardProps> = ({
    certificates,
    onSubmit,
}) => {
    const asset = useSelectedAsset();
    const areas = getCertificateAreas(asset?.assetId || -1);
    const stepNos = [1, 2, 3, 5, 6];

    // find ready-to-complete certificates for all areas
    const readyToBeCompletedByArea = React.useMemo(() => {
        const result: { [area: string]: TCertificate[] } = {};

        areas.forEach(area => {
            const filteredCertificates = certificates.filter((cert) => cert.area === area);
            result[area] = filteredCertificates.filter((cert) => {
                const readyToComplete = isAllComplete(cert);
                return readyToComplete && cert.status !== 'Completed';
            });
        });

        return result;
    }, [certificates]);

    const countCertificatesReadyToBeCompleted = (area: string): number => {
        return readyToBeCompletedByArea[area]?.length || 0;
    };

    const data = areas.map((area) => [
        area,
        ...stepNos.map(stepNo => getCertCount(certificates, area, stepNo)),
        countCertificatesReadyToBeCompleted(area),
    ]);

    const totalColCount = () => {
        const totalRow = [];
        for (let i = 0; i < headers.length - 1; i++) {
            let colTotal = 0;
            data.forEach((row) => {
                colTotal += Number(row[i + 1]);
            });
            totalRow.push(colTotal);
        }
        return totalRow;
    }

    data.push(['Total', ...totalColCount()]);

    const handleClick = (headerIndex: number, area?: string,) => () => {
        const stepTitle = headers[headerIndex];
        const headerToStepTitleMap: { [key: string]: string[] } = {
            'Broken Out': ['Joint Broken'],
            'Assembled': ['Joint Assembled'],
            'Tightened/Inspected': ['Joint Tightened', 'Joint Witnessed'],
            'Tested': ['Leak/Service Test'],
            'Started': ['Start Up'],
        };

        // const completedStepFilters = (headerIndex === 6) ? ['Ready to Complete'] : headerToStepTitleMap[stepTitle];
        const completedStepFilters = (headerIndex === 6) ? ['Start Up', '12 HR', '24 HR', '36 HR', '48 HR'] : headerToStepTitleMap[stepTitle];


        const filters: TCertificateFilters = {
            isolationNumber: [],
            maintenanceOrder: [],
            mc1: [],
            sh1: [],
            permitNumber: [],
            status: [],
            system: [],
            template: [],
            area: area ? [area] : [],
            completedStep: completedStepFilters,
        };

        onSubmit(filters);
    }

    return (
        <Accordion defaultOpen="1" size="md" className={classes.monitoringDashboard}>
            <Accordion.Item>
                <Accordion.Header targetId="1" title="Certificate Monitoring Dashboard" />
                <Accordion.Body accordionId="1">
                    <Table borderless>
                        <Table.Head>
                            <Table.HeaderRow>
                                {headers.map((header, index) => (
                                    <Table.Header key={index}>{header}</Table.Header>
                                ))}
                            </Table.HeaderRow>
                        </Table.Head>
                        <Table.Body>
                            {data.map((row, rowIndex) => (
                                <Table.Row rowIndex={rowIndex} key={`row-${rowIndex}`}>
                                    {row.map((cell, cellIndex) => (
                                        <Table.Cell key={cellIndex}>
                                            {typeof cell === 'number'
                                                ? (
                                                    <Button
                                                        className={(cellIndex === 6 || cellIndex === 5) ? classes.cellLightButton : ''}
                                                        level="primary"
                                                        theme="standard"
                                                        rounded='pill'
                                                        size='xs'
                                                        color={
                                                            cellIndex === 1
                                                                ? 'info'
                                                                : cellIndex === 2
                                                                    ? 'danger'
                                                                    : cellIndex === 3
                                                                        ? 'warning'
                                                                        : cellIndex === 4
                                                                            ? 'success'
                                                                            : cellIndex === 5
                                                                                ? 'light'
                                                                                : cellIndex === 6
                                                                                    ? 'light'
                                                                                    : 'dark'
                                                        }
                                                        onClick={(row[0].toString() === 'Total') ? (handleClick(cellIndex)) : (handleClick(cellIndex, row[0].toString()))}
                                                        disabled={cell === 0}
                                                    >
                                                        {cell}
                                                    </Button>
                                                )
                                                : cell}
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};
